// app/components/home/OurTeam.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function OurTeam() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const ourteamFixedBgrRef = useRef<HTMLDivElement>(null);

  // ✅ ใช้ path เดียวกับที่ Save ใช้จริง (window.location.pathname)
  const [pagePath, setPagePath] = useState<string>("/");
  // ✅ รู้ว่าอยู่ใน inline edit mode หรือไม่ (มาจาก EditPageInlineClient)
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
    setIsEditMode(window.__CMS_EDIT_ALLOWED__ === true);
  }, []);

  // ---- default text / image จาก i18n (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("OurTeam.title");
  const defaultContent = t("OurTeam.content");
  const defaultImg1 = "/assets/home/ourteam/img1.png";
  const defaultImg2 = "/assets/home/ourteam/img2.png";
  const defaultImg3 = "/assets/home/ourteam/img3.png";

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [content, setContent] = useState(defaultContent);
  const [img1, setImg1] = useState(defaultImg1);
  const [img2, setImg2] = useState(defaultImg2);
  const [img3, setImg3] = useState(defaultImg3);

  // เมื่อ locale เปลี่ยน → reset กลับค่า default ก่อน
  useEffect(() => {
    setTitle(defaultTitle);
    setContent(defaultContent);
    setImg1(defaultImg1);
    setImg2(defaultImg2);
    setImg3(defaultImg3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  useEffect(() => {
    if (!pagePath) return; // รอให้รู้ path จริงก่อน

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback: เคสเก่าๆ ที่มีแต่ selector ให้ลอง regex หา data-cms-key
          if (!key) {
            const match = e.selector?.match(/data-cms-key="([^"]+)"/);
            if (match) {
              key = match[1];
            }
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // text
        if (map["home.ourteam.title"]) {
          setTitle(map["home.ourteam.title"]);
        }
        if (map["home.ourteam.content"]) {
          setContent(map["home.ourteam.content"]);
        }

        // images
        if (map["home.ourteam.img1"]) {
          setImg1(map["home.ourteam.img1"]);
        }
        if (map["home.ourteam.img2"]) {
          setImg2(map["home.ourteam.img2"]);
        }
        if (map["home.ourteam.img3"]) {
          setImg3(map["home.ourteam.img3"]);
        }
      } catch (err) {
        console.warn("[OurTeam] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  // ---- fixed background เดิม ----
  useEffect(() => {
    const handleScroll = () => {
      if (!ourteamFixedBgrRef.current) return;
      const introTop =
        ourteamFixedBgrRef.current.getBoundingClientRect().top;
      if (introTop <= 0) {
        ourteamFixedBgrRef.current.classList.add("bg-fixed");
      } else {
        ourteamFixedBgrRef.current.classList.remove("bg-fixed");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      className="ourteam-wrap"
      ref={ourteamFixedBgrRef}
      sx={{
        py: { xs: 5, sm: 10, lg: 10 }
      }}
    >
      <Box>
        <Grid container>
          <Grid
            size={{ xs: 12, md: 6 }}
            data-aos="fade-right"
            sx={{
              pr: { xs: 0, md: 3 }
            }}
          >
            <Swiper
              spaceBetween={16}
              slidesPerView={1.2}
              dir="rtl"
              rewind
              // ✅ เวลาเข้า inline edit ให้หยุด autoplay + touch move
              autoplay={
                isEditMode
                  ? false
                  : {
                      delay: 5000,
                    }
              }
              allowTouchMove={!isEditMode}
              modules={[Autoplay]}
            >
              <SwiperSlide>
                <Image
                  src={img1}
                  title="our poonphol team"
                  alt="our poonphol team"
                  width={678}
                  height={442}
                  className="img-fluid"
                  data-cms-key="home.ourteam.img1"
                />
              </SwiperSlide>
              <SwiperSlide>
                <Image
                  src={img2}
                  title="our poonphol team"
                  alt="our poonphol team"
                  width={678}
                  height={442}
                  className="img-fluid"
                  data-cms-key="home.ourteam.img2"
                />
              </SwiperSlide>
              <SwiperSlide>
                <Image
                  src={img3}
                  title="our poonphol team"
                  alt="our poonphol team"
                  width={678}
                  height={442}
                  className="img-fluid"
                  data-cms-key="home.ourteam.img3"
                />
              </SwiperSlide>
            </Swiper>
          </Grid>
        </Grid>
      </Box>
      <Box className="ourteam-content" sx={{
        pt: { xs: 5, md: 0 }
      }}>
        <Container className="container">
          <Grid container>
            <Grid
              size={{ xs: 12, md: 6 }}
              offset={{ xs: 0, md: 6 }}
              sx={{
                pl: { xs: 0, md: 3 }
              }}
            >
              <Typography
                variant="h2"
                data-heading-tag="H2"
                data-aos="fade-up"
                data-cms-key="home.ourteam.title"
                sx={{
                  mb: 3,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {title}
              </Typography>
              <Typography
                variant="body1"
                sx={{ textIndent: 2 }}
                data-aos="fade-up"
                data-cms-key="home.ourteam.content"
              >
                {content}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}